/**
 * Client-side helpers for shared space RBAC (OWNER / EDITOR / VIEWER).
 * Authoritative enforcement also exists in DB RPCs (019_space_share_rbac.sql).
 */

export type SpaceMemberRole = "owner" | "editor" | "viewer";

export type ItemKind = "document" | "thread" | "topic";

export interface ItemOverrideMap {
  /** key: `${itemType}:${itemId}` → access level for current user */
  [key: string]: "editor" | "viewer";
}

export interface EffectiveItemActions {
  canView: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canLink: boolean;
}

function overrideKey(kind: ItemKind, itemId: string): string {
  return `${kind}:${itemId}`;
}

/**
 * @param spaceRole - current user's role in this space (owner = mastermind owner)
 * @param itemOwnerUserId - userId of the document/thread/topic row (creator)
 * @param spaceOwnerUserId - mastermind.user_id for the space
 */
export function effectiveItemActions(
  spaceRole: SpaceMemberRole | null,
  itemKind: ItemKind,
  itemId: string,
  itemOwnerUserId: string | null | undefined,
  currentUserId: string | undefined,
  spaceOwnerUserId: string | null | undefined,
  overrides: ItemOverrideMap | undefined
): EffectiveItemActions {
  const uid = currentUserId ?? "";
  const ownerUid = itemOwnerUserId ?? "";
  const so = spaceOwnerUserId ?? "";
  const ov = overrides?.[overrideKey(itemKind, itemId)];

  const isItemOwner = !!uid && !!ownerUid && ownerUid === uid;
  /** Legacy topic with no created_by: treat space owner as topic owner */
  const isLegacyTopicOwner =
    itemKind === "topic" && !ownerUid && !!uid && so && uid === so;

  if (!spaceRole) {
    return { canView: false, canEdit: false, canDelete: false, canLink: false };
  }

  if (isItemOwner || isLegacyTopicOwner) {
    return { canView: true, canEdit: true, canDelete: true, canLink: true };
  }

  if (ov === "viewer") {
    return { canView: true, canEdit: false, canDelete: false, canLink: false };
  }

  if (spaceRole === "owner") {
    return { canView: true, canEdit: true, canDelete: true, canLink: true };
  }

  if (ov === "editor" || spaceRole === "editor") {
    return { canView: true, canEdit: true, canDelete: false, canLink: true };
  }

  if (spaceRole === "viewer") {
    return { canView: true, canEdit: false, canDelete: false, canLink: false };
  }

  return { canView: true, canEdit: false, canDelete: false, canLink: false };
}

/** Whether the user may create new topics in this space (owner or editor only). */
export function canCreateTopicsInSpace(spaceRole: SpaceMemberRole | null): boolean {
  return spaceRole === "owner" || spaceRole === "editor";
}

/**
 * When true, space editors (not the mastermind owner) may only link items to the current space
 * (no extra private or other shared spaces). UI and DB enforce this.
 */
export function shouldRestrictEditorLinksToCurrentSharedSpace(
  isSharedSpace: boolean,
  spaceMemberRole: SpaceMemberRole | null,
  spaceOwnerUserId: string | null | undefined,
  currentUserId: string | undefined
): boolean {
  if (!isSharedSpace || !currentUserId) return false;
  if (spaceMemberRole !== "editor") return false;
  const owner = spaceOwnerUserId ?? "";
  return owner !== "" && currentUserId !== owner;
}
